import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../helpers/api/apiConfig';

interface MeasurementValue {
    id: number;
    valueType: string;
    numValue?: number | null;
    textValue?: string | null;
    meta?: Record<string, any> | null;
    processingStage?: string | null;
}

interface MeasurementFile {
    id: number;
    fileName: string;
    fileType?: string;
    fileSize?: number;
    mimeType?: string;
    signedUrl?: string;
    processingStage?: string | null;
}

interface MeasurementText {
    id: number;
    text: string;
    textType?: string;
    meta?: Record<string, any> | null;
    processingStage?: string | null;
    createdAt?: string;
}

interface MeasurementDetail {
    id: number;
    measurementCode: string;
    createdAt: string;
    measuredAt?: string | null;
    measuredTimezone?: string | null;
    measuredUtcOffsetMinutes?: number | null;
    timeSource?: string | null;
    timezoneSource?: string | null;
    geom?: { type: string; coordinates: number[] } | null;
    localPose?: Record<string, any> | null;
    area?: { id: number; name: string; areaType?: { id: number; name: string } } | null;
    device?: { id: number; label: string } | null;
    pattern?: { id: number; name: string; patternType?: string; coordSystem?: string } | null;
    patternPoint?: { id: number; label?: string; pointIndex?: number } | null;
    mission?: { id: number; name: string } | null;
    missionPoint?: { id: number; label?: string; pointIndex?: number } | null;
    values?: MeasurementValue[];
    files?: MeasurementFile[];
    texts?: MeasurementText[];
}

interface MeasurementReportState {
    measurementId?: number | null;
    measurement: MeasurementDetail | null;
    loading: boolean;
    error: string | null;
    files: MeasurementFile[];
    filesLoading: boolean;
    weather: any | null;
    weatherContext: any | null;
    weatherLoading: boolean;
    refreshWeather: () => Promise<void>;
    patternPoints: any[];
    patternPointsLoading: boolean;
    areaHierarchy: any[];
    areaHierarchyLoading: boolean;
    areaGeometry: any | null;
    areaGeometryLoading: boolean;
    areaType: any | null;
    texts: MeasurementText[];
}

const MeasurementReportContext = createContext<MeasurementReportState>({
    measurementId: null,
    measurement: null,
    loading: false,
    error: null,
    files: [],
    filesLoading: false,
    weather: null,
    weatherContext: null,
    weatherLoading: false,
    refreshWeather: async () => {},
    patternPoints: [],
    patternPointsLoading: false,
    areaHierarchy: [],
    areaHierarchyLoading: false,
    areaGeometry: null,
    areaGeometryLoading: false,
    areaType: null,
    texts: [],
});

const buildAreaHierarchy = (areaId: number, allAreas: any[]) => {
    const lookup = new Map<number, any>();
    allAreas.forEach((area) => lookup.set(area.id, area));
    const chain: any[] = [];
    let current = lookup.get(areaId);
    while (current) {
        chain.unshift(current);
        if (!current.parentAreaId) break;
        current = lookup.get(current.parentAreaId);
    }
    return chain;
};

const filterProcessed = <T extends { processingStage?: string | null }>(items?: T[]) =>
    (items || []).filter((item) => item.processingStage === 'processed');

const filterVisibleTexts = (items?: MeasurementText[]) =>
    (items || []).filter((item) =>
        item.processingStage === 'processed' ||
        item.textType === 'capture_metadata' ||
        item.textType === 'trigger_metadata'
    );

interface ProviderProps {
    measurementId?: number | null;
    children: React.ReactNode;
}

export const MeasurementReportProvider: React.FC<ProviderProps> = ({ measurementId, children }) => {
    const [measurement, setMeasurement] = useState<MeasurementDetail | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [files, setFiles] = useState<MeasurementFile[]>([]);
    const [filesLoading, setFilesLoading] = useState(false);
    const [weather, setWeather] = useState<any | null>(null);
    const [weatherContext, setWeatherContext] = useState<any | null>(null);
    const [weatherLoading, setWeatherLoading] = useState(false);
    const [patternPoints, setPatternPoints] = useState<any[]>([]);
    const [patternPointsLoading, setPatternPointsLoading] = useState(false);
    const [areaHierarchy, setAreaHierarchy] = useState<any[]>([]);
    const [areaHierarchyLoading, setAreaHierarchyLoading] = useState(false);
    const [areaGeometry, setAreaGeometry] = useState<any | null>(null);
    const [areaGeometryLoading, setAreaGeometryLoading] = useState(false);
    const [areaType, setAreaType] = useState<any | null>(null);
    const [texts, setTexts] = useState<MeasurementText[]>([]);

    useEffect(() => {
        let cancelled = false;
        if (!measurementId) {
            setMeasurement(null);
            setError(null);
            return;
        }

        setLoading(true);
        setError(null);

        axios
            .get(ENDPOINTS.MEASUREMENT_DETAIL(measurementId), { withCredentials: true })
            .then((res) => {
                if (cancelled) return;
                const data = res.data?.data || res.data;
                const filtered = {
                    ...data,
                    values: filterProcessed(data?.values),
                    files: filterProcessed(data?.files),
                    texts: filterVisibleTexts(data?.texts),
                };
                setMeasurement(filtered);
                setTexts(filtered.texts || []);
            })
            .catch((err) => {
                if (cancelled) return;
                console.error('[MeasurementReport] Failed to load measurement', err);
                setError('Failed to load measurement');
                setMeasurement(null);
                setTexts([]);
            })
            .finally(() => {
                if (cancelled) return;
                setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [measurementId]);

    useEffect(() => {
        let cancelled = false;
        if (!measurementId || !measurement) {
            setFiles([]);
            setWeather(null);
            setPatternPoints([]);
            setAreaHierarchy([]);
            setAreaGeometry(null);
            setAreaType(null);
            setTexts([]);
            return;
        }

        setFilesLoading(true);
        axios
            .get(ENDPOINTS.MEASUREMENT_FILES_BATCH_URLS(measurementId), { withCredentials: true })
            .then((res) => {
                if (cancelled) return;
                const fileList: MeasurementFile[] = res.data?.data?.files || [];
                setFiles(filterProcessed(fileList));
            })
            .catch((err) => {
                if (cancelled) return;
                console.warn('[MeasurementReport] Failed to load files, falling back to measurement files', err);
                setFiles(filterProcessed(measurement.files || []));
            })
            .finally(() => {
                if (cancelled) return;
                setFilesLoading(false);
            });

        setWeatherLoading(true);
        axios
            .get(ENDPOINTS.MEASUREMENT_WEATHER(measurementId), { withCredentials: true })
            .then((res) => {
                if (cancelled) return;
                const weatherData = res.data?.data?.weather || null;
                const contextData = res.data?.data?.dataContext || null;
                setWeather(weatherData);
                setWeatherContext(contextData);
            })
            .catch((err) => {
                if (cancelled) return;
                console.warn('[MeasurementReport] Failed to load weather', err);
                setWeather(null);
                setWeatherContext(null);
            })
            .finally(() => {
                if (cancelled) return;
                setWeatherLoading(false);
            });

        if (measurement.pattern?.id) {
            setPatternPointsLoading(true);
            axios
                .get(ENDPOINTS.PATTERN_POINTS(measurement.pattern.id), { withCredentials: true })
                .then((res) => {
                    if (cancelled) return;
                    const points = res.data?.data || res.data || [];
                    setPatternPoints(points);
                })
                .catch((err) => {
                    if (cancelled) return;
                    console.warn('[MeasurementReport] Failed to load pattern points', err);
                    setPatternPoints([]);
                })
                .finally(() => {
                    if (cancelled) return;
                    setPatternPointsLoading(false);
                });
        } else {
            setPatternPoints([]);
        }

        if (measurement.area?.id) {
            setAreaHierarchyLoading(true);
            axios
                .get(ENDPOINTS.AREAS_V2, { withCredentials: true, params: { include_archived: 'true' } })
                .then(async (res) => {
                    if (cancelled) return;
                    const allAreas = res.data?.data || res.data || [];
                    const hierarchy = buildAreaHierarchy(measurement.area!.id, allAreas);
                    const hierarchyWithGeometry = await Promise.all(
                        hierarchy.map(async (area, hierarchyDepth) => {
                            try {
                                const detailRes = await axios.get(ENDPOINTS.AREA_DETAIL(area.id), {
                                    withCredentials: true,
                                });
                                const detail = detailRes.data?.data || detailRes.data;
                                return { ...area, ...detail, hierarchyDepth };
                            } catch (err) {
                                console.warn(`[MeasurementReport] Failed to load hierarchy area ${area.id}`, err);
                                return { ...area, hierarchyDepth };
                            }
                        })
                    );
                    if (cancelled) return;
                    setAreaHierarchy(hierarchyWithGeometry);
                })
                .catch((err) => {
                    if (cancelled) return;
                    console.warn('[MeasurementReport] Failed to build area hierarchy', err);
                    setAreaHierarchy([]);
                })
                .finally(() => {
                    if (cancelled) return;
                    setAreaHierarchyLoading(false);
                });

            setAreaGeometryLoading(true);
            axios
                .get(ENDPOINTS.AREA_DETAIL(measurement.area.id), { withCredentials: true })
                .then((res) => {
                    if (cancelled) return;
                    const areaData = res.data?.data || res.data;
                    setAreaGeometry(areaData?.geom || null);
                    setAreaType(areaData?.areaType || null);
                })
                .catch((err) => {
                    if (cancelled) return;
                    console.warn('[MeasurementReport] Failed to load area geometry', err);
                    setAreaGeometry(null);
                    setAreaType(null);
                })
                .finally(() => {
                    if (cancelled) return;
                    setAreaGeometryLoading(false);
                });
        } else {
            setAreaHierarchy([]);
            setAreaGeometry(null);
            setAreaType(null);
        }

        return () => {
            cancelled = true;
        };
    }, [measurementId, measurement]);

    const refreshWeather = async () => {
        if (!measurementId) {
            console.warn('[MeasurementReport] Cannot refresh weather: no measurementId');
            return;
        }

        setWeatherLoading(true);
        try {
            const res = await axios.post(
                ENDPOINTS.MEASUREMENT_WEATHER_REFRESH(measurementId),
                {},
                { withCredentials: true }
            );
            const weatherData = res.data?.data?.weather || null;
            const contextData = res.data?.data?.dataContext || null;
            setWeather(weatherData);
            setWeatherContext(contextData);
            console.log('[MeasurementReport] Weather refreshed successfully');
        } catch (err: any) {
            console.error('[MeasurementReport] Failed to refresh weather:', err);
            // Don't clear existing weather on error, just log it
            if (err.response?.status === 400) {
                // GPS coordinates missing - this is expected for some measurements
                console.warn('[MeasurementReport] Cannot fetch weather: measurement has no GPS coordinates');
            }
        } finally {
            setWeatherLoading(false);
        }
    };

    const value = useMemo(
        () => ({
            measurementId,
            measurement,
            loading,
            error,
            files,
            filesLoading,
            weather,
            weatherContext,
            weatherLoading,
            refreshWeather,
            patternPoints,
            patternPointsLoading,
            areaHierarchy,
            areaHierarchyLoading,
            areaGeometry,
            areaGeometryLoading,
            areaType,
            texts,
        }),
        [
            measurementId,
            measurement,
            loading,
            error,
            files,
            filesLoading,
            weather,
            weatherContext,
            weatherLoading,
            patternPoints,
            patternPointsLoading,
            areaHierarchy,
            areaHierarchyLoading,
            areaGeometry,
            areaGeometryLoading,
            areaType,
            texts,
        ],
    );

    return <MeasurementReportContext.Provider value={value}>{children}</MeasurementReportContext.Provider>;
};

export const useMeasurementReport = () => useContext(MeasurementReportContext);

export default MeasurementReportContext;
